import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardText, ArrowClockwise } from "@phosphor-icons/react";

const fmtTime = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return String(iso); }
};

const KIND_TONE = {
    audit: "bg-cobalt/10 text-cobalt",
    impersonation: "bg-splash/30 text-ink",
};

const TYPE_FILTERS = [
    { key: "all",           label: "All events" },
    { key: "pii_reveal",    label: "PII reveals" },
    { key: "csv_export",    label: "CSV exports" },
    { key: "unsuppress",    label: "Unsuppressions" },
    { key: "impersonation", label: "Impersonations" },
];

export default function AdminAuditLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [typeFilter, setTypeFilter] = useState("all");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (typeFilter !== "all") params.types = typeFilter;
            if (q.trim()) params.q = q.trim();
            const r = await api.get("/admin/audit-log", { params });
            setRows(r.data.items || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load audit log");
        } finally { setLoading(false); }
    }, [q, typeFilter]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-audit-log">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1">DPDPA compliance</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-1">Audit log</h1>
            <p className="text-sm text-[#525252] mb-6">
                PII reveals · CSV exports · suppression unlocks · impersonation sessions.
            </p>

            <div className="flex items-center gap-2 mb-4 flex-wrap">
                {TYPE_FILTERS.map((t) => (
                    <button key={t.key}
                            onClick={() => setTypeFilter(t.key)}
                            data-testid={`audit-filter-${t.key}`}
                            className={`text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border transition-colors ${
                                typeFilter === t.key ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                            }`}>
                        {t.label}
                    </button>
                ))}
                <input value={q}
                       onChange={(e) => setQ(e.target.value)}
                       onKeyDown={(e) => e.key === "Enter" && load()}
                       data-testid="audit-search"
                       placeholder="Search email / user / reason…"
                       className="border border-[#E5E5E5] px-3 py-1.5 text-xs focus:outline-none focus:border-ink flex-1 min-w-[220px]" />
                <button onClick={load} data-testid="audit-refresh"
                        className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 border border-[#E5E5E5] hover:border-ink inline-flex items-center gap-1">
                    <ArrowClockwise size={12} weight="bold" /> Reload
                </button>
            </div>

            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && rows.length === 0 && (
                <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                    <ClipboardText size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                    <p className="text-sm text-[#525252]">No events yet — audit log records will appear here as they happen.</p>
                </div>
            )}
            {rows.length > 0 && (
                <>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-3">
                    {rows.map((r, i) => (
                        <div key={`${r.id}-${i}`} data-testid={`audit-card-${i}`} className="bg-white border border-[#E5E5E5] p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                    <p className="text-xs font-mono truncate">{r.type || "—"}</p>
                                    <p className="text-[10px] text-[#A3A3A3]">{fmtTime(r.created_at)}</p>
                                </div>
                                <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 shrink-0 ${KIND_TONE[r.kind] || KIND_TONE.audit}`}>
                                    {r.kind}
                                </span>
                            </div>
                            <div className="pt-2 border-t border-[#F5F5F5] space-y-1 text-xs">
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-14 shrink-0">Actor</span>
                                    <span className="truncate">{r.actor || "—"}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-14 shrink-0">Target</span>
                                    <span className="truncate">{r.target || "—"}</span>
                                </div>
                                {r.reason && (
                                    <div className="flex items-start gap-2">
                                        <span className="text-[9px] uppercase tracking-wider text-[#A3A3A3] w-14 shrink-0 mt-0.5">Reason</span>
                                        <span className="text-[#525252]">{r.reason}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                {/* Desktop table */}
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm min-w-[820px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["Time", "Kind", "Type", "Actor", "Target", "Reason"].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={`${r.id}-${i}`} data-testid={`audit-row-${i}`} className="border-b border-[#F5F5F5]">
                                    <td className="px-4 py-3 text-xs">{fmtTime(r.created_at)}</td>
                                    <td className="px-4 py-3">
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 ${KIND_TONE[r.kind] || KIND_TONE.audit}`}>
                                            {r.kind}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-xs font-mono">{r.type || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{r.actor || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{r.target || "—"}</td>
                                    <td className="px-4 py-3 text-xs text-[#525252] max-w-[240px] truncate" title={r.reason || ""}>
                                        {r.reason || "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                </>
            )}
        </div>
    );
}
